/**
 * Counting-line geometry for the editor — direction labels and endpoint hit-testing.
 *
 * Client-pixel <-> normalised-frame maths is NOT redefined here: `roiGeometry.ts` already owns
 * it, including why `object-fit: contain` forces every click through the *content* rect rather
 * than the element rect. This module adds only what a line needs that an area does not.
 *
 * `directionLabels` is the client-side twin of `backend/tripwire_lines.direction_labels`.
 * Two implementations of one rule, because the burned-in overlay is drawn server-side and the
 * editor previews client-side — the same split `roiGeometry.ts` and `detection_roi.py` already
 * live with. Both are pinned against the same table of cases in their unit tests; change one
 * and you must change the other.
 */
import { toClient, type ContentRect, type NormPoint } from './roiGeometry';

/** Mirrors tripwire_lines.MAX_LINES_PER_FEED — the backend 409s past this. */
export const MAX_LINES_PER_FEED = 4;

/** How close to an endpoint a pointer has to land to grab it. */
export const GRAB_THRESHOLD_PX = 14;

export type Line = [NormPoint, NormPoint];

/**
 * The normal pointing at the side a crossing counts as 'in'.
 *
 * `flow_tracker.crossing_direction` calls a track 'in' (at orientation +1) when it ends where
 * `_side(a, b, p) < 0`. Substituting `p = a + (dy, -dx)` into that expression gives
 * `-(dx^2 + dy^2)`, always negative — so `(dy, -dx)` is the in-side normal, flipped by
 * orientation.
 */
export function inNormal(line: Line, orientation: number = 1): [number, number] {
  const [[ax, ay], [bx, by]] = line;
  const sign = orientation >= 0 ? 1 : -1;
  return [(by - ay) * sign, -(bx - ax) * sign];
}

/**
 * `[inLabel, outLabel]` — RIGHT/LEFT or DOWN/UP, named by the dominant axis of the in-normal.
 * Ties (a 45-degree line) go to the horizontal so the answer never depends on float noise.
 * Image y grows downward, so a positive ny reads as DOWN.
 */
export function directionLabels(line: Line, orientation: number = 1): [string, string] {
  const [nx, ny] = inNormal(line, orientation);
  if (Math.abs(nx) >= Math.abs(ny)) {
    return nx > 0 ? ['RIGHT', 'LEFT'] : ['LEFT', 'RIGHT'];
  }
  return ny > 0 ? ['DOWN', 'UP'] : ['UP', 'DOWN'];
}

/** Midpoint of a line, in element-relative pixels. */
export function midpointClient(line: Line, rect: ContentRect): [number, number] {
  const [x1, y1] = toClient(line[0][0], line[0][1], rect);
  const [x2, y2] = toClient(line[1][0], line[1][1], rect);
  return [(x1 + x2) / 2, (y1 + y2) / 2];
}

/** Where the in-side arrow should end, `length` px from the midpoint along the in-normal. */
export function arrowTip(
  line: Line, orientation: number, rect: ContentRect, length: number = 26,
): [number, number] {
  const [mx, my] = midpointClient(line, rect);
  // Taken in client space so the arrow stays perpendicular on screen even when the picture is
  // scaled non-uniformly by the letterbox maths.
  const [x1, y1] = toClient(line[0][0], line[0][1], rect);
  const [x2, y2] = toClient(line[1][0], line[1][1], rect);
  const sign = orientation >= 0 ? 1 : -1;
  const nx = (y2 - y1) * sign;
  const ny = -(x2 - x1) * sign;
  const len = Math.hypot(nx, ny) || 1;
  return [mx + (nx / len) * length, my + (ny / len) * length];
}

/** True when the two ends are far enough apart to be a line the backend will accept. */
export function isDrawable(line: Line | null): boolean {
  if (!line) return false;
  const [[x1, y1], [x2, y2]] = line;
  return x1 !== x2 || y1 !== y2;
}
